import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import MetricsOverview from './components/MetricsOverview';
import DownloadsChart from './components/DownloadsChart';
import GeographicHeatMap from './components/GeographicHeatMap';
import PageClicksChart from './components/PageClicksChart';
import SessionAnalytics from './components/SessionAnalytics';
import metricsService from '../../services/metricsService';
import { useAuth } from '../../contexts/AuthContext';
import { useArtistData } from '../../hooks/useArtistData';

const ArtistMetricsDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { artistData } = useArtistData();
  const [metricsData, setMetricsData] = useState(null);
  const [downloadMetrics, setDownloadMetrics] = useState(null);
  const [pageClickMetrics, setPageClickMetrics] = useState(null);
  const [sessionMetrics, setSessionMetrics] = useState(null);
  const [timeRange, setTimeRange] = useState('30_days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract artist brand colors for theming
  const highlightColor = artistData?.profile?.highlight_color || artistData?.profile?.highlightColor || '#6366F1';
  const secondaryColor = artistData?.profile?.secondary_color || artistData?.profile?.secondaryColor || '#6366F1';

  // Sections menu configuration
  const sections = [
    {
      id: 'profile',
      name: 'Profile',
      icon: 'User',
      description: 'Basic information and contact details',
      required: true,
      completed: !!(artistData?.profile?.name && artistData?.profile?.email),
      route: '/artist-content-management'
    },
    {
      id: 'promotion',
      name: 'Promotion',
      icon: 'Megaphone',
      description: 'App images and featured content',
      required: true,
      completed: !!(artistData?.promotion?.splashImage && artistData?.promotion?.logo),
      route: '/artist-content-management'
    },
    {
      id: 'about',
      name: 'About',
      icon: 'FileText',
      description: 'Biography and booking information',
      required: true,
      completed: !!(artistData?.about?.aboutText && artistData?.about?.bookingContact?.name),
      route: '/artist-content-management'
    },
    {
      id: 'calendar',
      name: 'Calendar',
      icon: 'Calendar',
      description: 'Events and performances',
      required: false,
      completed: artistData?.calendar?.length > 0,
      route: '/artist-content-management'
    },
    {
      id: 'work',
      name: 'Work',
      icon: 'Briefcase',
      description: 'Portfolio and projects',
      required: false,
      completed: artistData?.work?.length > 0,
      route: '/artist-content-management'
    },
    {
      id: 'shop',
      name: 'Shop',
      icon: 'ShoppingCart',
      description: 'Merchandise and products',
      required: false,
      completed: Object.values(artistData?.shop || {})?.some(Boolean),
      route: '/artist-content-management'
    },
    {
      id: 'metrics',
      name: 'Metrics',
      icon: 'BarChart3',
      description: 'Mobile app analytics and insights',
      required: false,
      completed: false,
      active: true
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: 'Bell',
      description: 'Push notifications to mobile users',
      required: false,
      completed: false,
      route: '/artist-notifications-manager'
    }
  ];

  const timeRangeOptions = [
    { value: '7_days', label: '7 Days' },
    { value: '30_days', label: '30 Days' },
    { value: '90_days', label: '90 Days' },
    { value: 'all_time', label: 'All Time' }
  ];

  // Handle section navigation
  const handleSectionClick = (section) => {
    if (section?.route) {
      navigate(section?.route);
    }
  };

  // Load all metrics data
  const loadMetrics = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);

    try {
      const [overviewResult, downloadsResult, clicksResult, sessionResult] = await Promise.all([
        metricsService?.getArtistMetrics(user?.id, timeRange),
        metricsService?.getDownloadMetrics(user?.id, timeRange),
        metricsService?.getPageClickMetrics(user?.id, timeRange),
        metricsService?.getSessionMetrics(user?.id, timeRange)
      ]);

      if (overviewResult?.error) throw new Error(overviewResult.error);
      if (downloadsResult?.error) throw new Error(downloadsResult.error);
      if (clicksResult?.error) throw new Error(clicksResult.error);
      if (sessionResult?.error) throw new Error(sessionResult.error);

      setMetricsData(overviewResult?.data);
      setDownloadMetrics(downloadsResult?.data);
      setPageClickMetrics(clicksResult?.data);
      setSessionMetrics(sessionResult?.data);
    } catch (err) {
      setError(err?.message || 'Failed to load metrics data');
    } finally {
      setLoading(false);
    }
  };

  // Export metrics data
  const handleExportData = () => {
    if (!metricsData) return;

    const exportData = {
      overview: metricsData,
      downloads: downloadMetrics,
      pageClicks: pageClickMetrics,
      sessions: sessionMetrics,
      timeRange,
      exportedAt: new Date()?.toISOString(),
      artist: {
        name: userProfile?.full_name,
        email: userProfile?.email
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `artist-metrics-${timeRange}-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadMetrics();
  }, [user?.id, timeRange]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    const subscription = metricsService?.subscribeToMetrics(user?.id, (payload) => {
      // Refresh data when metrics are updated
      loadMetrics();
    });

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, [user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div
                  className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                  style={{ borderColor: `${highlightColor}40`, borderTopColor: 'transparent', borderLeftColor: highlightColor }}
                ></div>
                <p className="text-text-secondary">Loading metrics...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                <p className="text-destructive mb-4">{error}</p>
                <Button onClick={loadMetrics}>Try Again</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Section Navigation */}
            <div className="lg:col-span-1">
              <div
                className="bg-surface rounded-lg border p-4 sticky top-24"
                style={{ borderColor: `${highlightColor}30` }}
              >
                <div className="flex items-center space-x-2 mb-4">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: highlightColor }}
                  />
                  <h3 className="text-lg font-semibold text-text-primary">Sections</h3>
                </div>
                
                <nav className="space-y-2">
                  {sections?.map((section) => (
                    <button
                      key={section?.id}
                      onClick={() => handleSectionClick(section)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                        section?.active
                          ? 'text-white' :'hover:bg-muted text-text-primary'
                      }`}
                      style={section?.active ? {
                        background: `linear-gradient(135deg, ${highlightColor}E6, ${secondaryColor}CC)`,
                        boxShadow: `0 2px 8px ${highlightColor}40`
                      } : {}}
                    >
                      <div className="relative">
                        <Icon 
                          name={section?.icon} 
                          size={18} 
                          className={section?.active ? 'text-white' : 'text-text-secondary'}
                        />
                        {section?.completed && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full flex items-center justify-center">
                            <Icon name="Check" size={8} color="white" />
                          </div>
                        )}
                        {section?.route && !section?.active && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full flex items-center justify-center">
                            <Icon name="ExternalLink" size={6} color="white" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{section?.name}</span>
                          {section?.required && (
                            <span
                              className="text-xs"
                              style={{ color: section?.active ? 'rgba(255,255,255,0.8)' : secondaryColor }}
                            >*</span>
                          )}
                        </div>
                        <p className="text-xs opacity-70 truncate">{section?.description}</p>
                      </div>
                    </button>
                  ))}
                </nav>

                {/* Color accent strip at bottom of nav */}
                <div
                  className="mt-4 h-px rounded-full opacity-40"
                  style={{ background: `linear-gradient(to right, ${highlightColor}, ${secondaryColor}, transparent)` }}
                />

                {/* Artist Info */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${highlightColor}, ${secondaryColor})` }}
                    >
                      <Icon name="Palette" size={16} color="white" />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{artistData?.name || userProfile?.full_name || 'Artist'}</p>
                      <p className="text-xs text-text-secondary capitalize">{artistData?.artistType || userProfile?.artist_type || 'visual'} Artist</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              {/* Header Section */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="flex items-center space-x-3 mb-1">
                    <div
                      className="w-1 h-8 rounded-full"
                      style={{ background: `linear-gradient(to bottom, ${highlightColor}, ${secondaryColor})` }}
                    />
                    <h1 className="text-3xl font-bold text-text-primary">Mobile App Metrics</h1>
                  </div>
                  <p className="text-text-secondary mt-1 ml-4">
                    Track your mobile app performance and user engagement
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  {/* Time Range Selector */}
                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e?.target?.value)}
                    className="px-4 py-2 border border-border rounded-lg bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {timeRangeOptions?.map(option => (
                      <option key={option?.value} value={option?.value}>
                        {option?.label}
                      </option>
                    ))}
                  </select>
                  
                  <Button
                    variant="outline"
                    onClick={handleExportData}
                    iconName="Download"
                    iconPosition="left"
                    iconSize={16}
                    disabled={!metricsData}
                  >
                    Export Data
                  </Button>
                  
                  <Button
                    variant="default"
                    onClick={loadMetrics}
                    iconName="RefreshCw"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Metrics Overview */}
              <MetricsOverview metricsData={metricsData} />

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                {/* Downloads Chart */}
                <DownloadsChart 
                  data={metricsData?.chartData} 
                  timeRange={timeRange}
                />
                
                {/* Geographic Heat Map */}
                <GeographicHeatMap 
                  data={downloadMetrics} 
                  totalDownloads={metricsData?.totalDownloads}
                />
                
                {/* Page Clicks Chart */}
                <PageClicksChart 
                  data={pageClickMetrics} 
                />
                
                {/* Session Analytics */}
                <SessionAnalytics 
                  data={sessionMetrics}
                  averageDuration={metricsData?.averageSessionDuration}
                />
              </div>

              {/* Additional Analytics Information */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-surface rounded-lg border border-border p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${highlightColor}20` }}
                    >
                      <Icon name="Smartphone" size={20} style={{ color: highlightColor }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary">Mobile Insights</h3>
                      <p className="text-sm text-text-secondary">App usage patterns</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-text-primary mb-2">
                    {metricsData?.totalActiveUsers || 0}
                  </div>
                  <p className="text-sm text-text-secondary">Active users this period</p>
                </div>

                <div className="bg-surface rounded-lg border border-border p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                      <Icon name="TrendingUp" size={20} className="text-success" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary">Growth Rate</h3>
                      <p className="text-sm text-text-secondary">Period over period</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-success mb-2">
                    +{((metricsData?.totalDownloads || 0) * 0.15)?.toFixed(0)}%
                  </div>
                  <p className="text-sm text-text-secondary">Download growth</p>
                </div>

                <div className="bg-surface rounded-lg border border-border p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${secondaryColor}20` }}
                    >
                      <Icon name="Clock" size={20} style={{ color: secondaryColor }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary">Avg. Session</h3>
                      <p className="text-sm text-text-secondary">Time per session</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-text-primary mb-2">
                    {metricsData?.averageSessionDuration || 0}m
                  </div>
                  <p className="text-sm text-text-secondary">Minutes per session</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistMetricsDashboard;